import { useState } from "react";
import { ZodError } from "zod";

/**
 * Interface pour les options d'affichage des erreurs Zod
 */
export interface ZodErrorDisplayOptions {
  /** Afficher tous les messages d'erreur ou seulement le premier */
  showAllErrors?: boolean;
  /** Préfixe à ajouter avant le message d'erreur */
  prefix?: string;
  /** Séparateur entre les messages d'erreur multiples */
  separator?: string;
  /** Fonction de toast personnalisée */
  toastFunction?: (
    message: string,
    options: { variant: "error" | "success" | "warning" | "info" }
  ) => void;
  /** Options du toast */
  toastOptions?: {
    variant: "error" | "success" | "warning" | "info";
    [key: string]: any;
  };
  /** Callback appelé avec l'erreur formatée */
  onError?: (formattedError: any) => void;
  /** Message par défaut pour les erreurs non-Zod */
  defaultErrorMessage?: string;
}

/**
 * Formate et affiche les erreurs Zod de manière conviviale
 * @param error - L'erreur à traiter
 * @param options - Options de configuration
 */
export const handleZodError = (
  error: unknown,
  options: ZodErrorDisplayOptions = {}
) => {
  const {
    showAllErrors = false,
    prefix = "",
    separator = ", ",
    toastFunction,
    toastOptions = { variant: "error" },
    onError,
    defaultErrorMessage = "Erreur de validation",
  } = options;

  if (error instanceof ZodError) {
    // Formater l'erreur pour le debugging
    const formattedError = error.format();
    // console.log("Validation error:", formattedError);

    // Appeler le callback si fourni
    if (onError) {
      onError(formattedError);
    }

    // Construire le message d'erreur
    let errorMessage: string;

    if (showAllErrors) {
      // Afficher tous les messages d'erreur
      const allMessages = error.errors.map((err) => err.message);
      errorMessage = prefix + allMessages.join(separator);
    } else {
      // Afficher seulement le premier message d'erreur
      errorMessage = prefix + error.errors[0].message;
    }

    // Afficher le toast si une fonction est fournie
    if (toastFunction) {
      toastFunction(errorMessage, toastOptions);
    }

    return {
      isZodError: true,
      message: errorMessage,
      formattedError,
      allErrors: error.errors,
    };
  } else {
    // Gérer les erreurs non-Zod
    const errorMessage = defaultErrorMessage;

    if (toastFunction) {
      toastFunction(errorMessage, toastOptions);
    }

    return {
      isZodError: false,
      message: errorMessage,
      originalError: error,
    };
  }
};

/**
 * Version simplifiée pour une utilisation rapide avec useToast
 * @param error - L'erreur à traiter
 * @param useToast - Fonction useToast
 * @param options - Options supplémentaires
 */
export const displayZodError = (
  error: unknown,
  useToast: (
    message: string,
    options: { variant: "error" | "success" | "warning" | "info" }
  ) => void,
  options: Omit<ZodErrorDisplayOptions, "toastFunction"> = {}
) => {
  return handleZodError(error, {
    ...options,
    toastFunction: useToast,
  });
};

/**
 * Récupère tous les messages d'erreur Zod sans affichage
 * @param error - L'erreur Zod
 * @returns Array des messages d'erreur
 */
export const getZodErrorMessages = (error: ZodError): string[] => {
  return error.errors.map((err) => err.message);
};

/**
 * Récupère les erreurs Zod groupées par chemin de champ
 * @param error - L'erreur Zod
 * @returns Objet avec les erreurs groupées par chemin
 */
export const getZodErrorsByPath = (
  error: ZodError
): Record<string, string[]> => {
  const errorsByPath: Record<string, string[]> = {};

  error.errors.forEach((err) => {
    const path = err.path.join(".");
    if (!errorsByPath[path]) {
      errorsByPath[path] = [];
    }
    errorsByPath[path].push(err.message);
  });

  return errorsByPath;
};

export const STRING_VALIDATION_MESSAGES = {
  required_error: "Le champ est requis",
  invalid_type_error: "Le champ doit être une chaine de caractères",
};

export const useValidationErrors = () => {
  const [error, setError] = useState<ZodError | null>(null);

  function getFieldErrors(fieldName: string) {
    if (!error) return [];
    return error.errors
      .filter((e) => e.path[0] === fieldName)
      .map((e) => e.message);
  }

  function resetError() {
    setError(null);
  }

  return { error, setError, getFieldErrors, resetError };
};

export const useYupValidationErrors = () => {
  const [error, setError] = useState<any | null>(null);

  function getFieldErrors(fieldName: string) {
    if (!error) return [];
    // console.log(error.errors);
    console.log(error.errors);
    return error.errors as string[];
  }

  function getFieldsErrors(): string[] {
    if (!error) return [];
    console.log(error.errors);
    return error.errors as string[];
  }

  function resetError() {
    setError(null);
  }

  return { error, setError, getFieldErrors, resetError, getFieldsErrors };
};
